import type { OrderSheetRow, PriceBreak } from '../types/nursery';

/**
 * The order sheet's arrangement: rows grouped by tree type (pot size, bare
 * root grade), then by crop, so the page reads like the printed order form
 * staff already know — and the filters that narrow it.
 */

export type SheetShow = 'all' | 'free' | 'ordered';

export interface SheetFilters {
  show: SheetShow;
  search: string;
  /** A tree type id, or null for every size. */
  treeTypeId: number | null;
  /** A crop as the sheet heads it ("Lemon", or "Other" for none); null or absent for every crop. */
  crop?: string | null;
  /** Hide stock items that are sold under a product, so the product stands for them. */
  productsOnly?: boolean;
}

/** A stock item sold under an active product. */
export const isProductMember = (row: OrderSheetRow) => row.product_id == null && (row.in_products?.length ?? 0) > 0;

export interface SheetCrop {
  crop: string;
  rows: OrderSheetRow[];
}

export interface SheetSection {
  treeTypeId: number;
  treeTypeName: string;
  treeTypeCode: string;
  crops: SheetCrop[];
}

/** Rows with no crop on their cultivar. Sorted after every named crop. */
export const NO_CROP = 'Other';

/** The crop a row is listed under. */
const cropOf = (row: OrderSheetRow) => row.crop_type_name?.trim() || NO_CROP;

/**
 * What a typed quantity means: blank is 0 (remove the line), digits are that
 * many, anything else is null — not a quantity.
 */
export function parseQuantity(text: string): number | null {
  const t = text.trim();
  if (t === '') return 0;
  if (!/^\d{1,7}$/.test(t)) return null;
  return parseInt(t, 10);
}

/** What a line is, for the server: a stock item or an umbrella product. */
export function rowSubject(row: OrderSheetRow): { stock_item_id: number } | { product_id: number } {
  return row.product_id != null ? { product_id: row.product_id } : { stock_item_id: row.stock_item_id as number };
}

/**
 * Narrow the rows. `ordered` is the quantity each row holds right now (typed
 * or saved), by key. A row with a quantity survives the stock filter — hiding
 * something already on the order would read as though it had been removed.
 * The same goes for a product's member under "Products only".
 */
export function filterSheet(rows: OrderSheetRow[], filters: SheetFilters, ordered: Record<string, number>): OrderSheetRow[] {
  const words = filters.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    const qty = ordered[row.key] ?? 0;
    if (filters.treeTypeId != null && row.tree_type_id !== filters.treeTypeId) return false;
    if (filters.crop != null && cropOf(row) !== filters.crop) return false;
    if (filters.show === 'ordered' && qty <= 0) return false;
    if (filters.show === 'free' && row.free <= 0 && qty <= 0) return false;
    if (filters.productsOnly && isProductMember(row) && qty <= 0) return false;
    if (words.length) {
      const text = [row.name, row.rootstock_name, row.crop_type_name, row.sku_code, row.tree_type_code, row.tree_type_name]
        .filter(Boolean).join(' ').toLowerCase();
      if (!words.every((w) => text.includes(w))) return false;
    }
    return true;
  });
}

const byText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/**
 * Tree types in their catalogue order, crops alphabetically ("Other" last),
 * and within a crop the umbrella products first, then stock items by name and
 * rootstock.
 */
export function groupSheet(rows: OrderSheetRow[]): SheetSection[] {
  const sections = new Map<number, { section: SheetSection; sort: number; crops: Map<string, OrderSheetRow[]> }>();
  for (const row of rows) {
    let entry = sections.get(row.tree_type_id);
    if (!entry) {
      entry = {
        section: { treeTypeId: row.tree_type_id, treeTypeName: row.tree_type_name, treeTypeCode: row.tree_type_code, crops: [] },
        sort: row.tree_type_sort,
        crops: new Map(),
      };
      sections.set(row.tree_type_id, entry);
    }
    const crop = cropOf(row);
    const list = entry.crops.get(crop);
    if (list) list.push(row);
    else entry.crops.set(crop, [row]);
  }

  return [...sections.values()]
    .sort((a, b) => a.sort - b.sort || byText(a.section.treeTypeName, b.section.treeTypeName))
    .map(({ section, crops }) => ({
      ...section,
      crops: [...crops.entries()]
        .sort(([a], [b]) => Number(a === NO_CROP) - Number(b === NO_CROP) || byText(a, b))
        .map(([crop, cropRows]) => ({
          crop,
          rows: [...cropRows].sort((a, b) =>
            Number(a.product_id == null) - Number(b.product_id == null)
            || byText(a.name, b.name)
            || byText(a.rootstock_name ?? '', b.rootstock_name ?? '')),
        })),
    }));
}

/** The tree types on the sheet, in catalogue order, for the size filter. */
export function sheetTreeTypes(rows: OrderSheetRow[]): { id: number; name: string }[] {
  return groupSheet(rows).map((s) => ({ id: s.treeTypeId, name: s.treeTypeName }));
}

/** The crops on the sheet, alphabetically with "Other" last, for the crop filter. */
export function sheetCrops(rows: OrderSheetRow[]): string[] {
  return [...new Set(rows.map(cropOf))]
    .sort((a, b) => Number(a === NO_CROP) - Number(b === NO_CROP) || byText(a, b));
}

/**
 * A barcode as compared: no spaces, and an all-digit code without its leading
 * zeros, so a 12-digit UPC-A and the same code read as a 13-digit EAN match.
 */
export function barcodeKey(code: string): string {
  const t = code.replace(/\s+/g, '');
  return /^\d+$/.test(t) ? t.replace(/^0+(?=\d)/, '') : t.toUpperCase();
}

/**
 * The row a scanned barcode adds a tree of: a row's own barcode first (a
 * product's before a stock item's, as purchase order import matches), then a
 * retailer's barcode matched to it. null when nothing carries it.
 */
export function findRowByBarcode(rows: OrderSheetRow[], scanned: string): OrderSheetRow | null {
  const want = barcodeKey(scanned);
  if (!want) return null;
  const own = rows.filter((r) => r.barcode && barcodeKey(r.barcode) === want);
  if (own.length) return own.find((r) => r.product_id != null) ?? own[0];
  return rows.find((r) => (r.retailer_barcodes ?? []).some((c) => barcodeKey(c) === want)) ?? null;
}

/** Lines and trees entered, from the quantities by key. */
export function sheetCounts(ordered: Record<string, number>): { lines: number; trees: number } {
  let lines = 0;
  let trees = 0;
  for (const qty of Object.values(ordered)) {
    if (qty > 0) { lines += 1; trees += qty; }
  }
  return { lines, trees };
}

/**
 * The price a quantity of a row is sold at, before the tier discount: the
 * highest break at or below it (a box still empty reads as one). null when
 * the customer's list does not price it. The server prices the saved line
 * the same way and its figure is the one kept; this is for the "Show prices"
 * switch only.
 */
export function priceForQuantity(breaks: PriceBreak[] | undefined, quantity: number): number | null {
  const want = Math.max(quantity, 1);
  let price: number | null = null;
  for (const b of breaks ?? []) {
    if (b.min_quantity <= want) price = b.unit_price;
  }
  return price;
}

/** A price less a tier discount, to the cent. */
export const afterDiscount = (price: number, discountPct: number) =>
  Math.round(price * (1 - (discountPct || 0) / 100) * 100) / 100;

/**
 * How many of a row's quantity can be filled from stock now, and how many wait
 * in line for stock that becomes ready (first come, first served). Trees
 * already allocated to this order are its own; of the rest, orders ahead of
 * this one in the line take what is free before it does.
 */
export function waitingInLine(row: OrderSheetRow, quantity: number): { now: number; waiting: number; ahead: number } {
  const allocated = row.allocated ?? 0;
  const ahead = row.wanted_ahead ?? 0;
  const available = Math.max(row.free - ahead, 0);
  const toFill = Math.max(quantity - allocated, 0);
  const now = Math.min(toFill, available);
  return { now, waiting: toFill - now, ahead };
}
